import {
  addRunSheetPart,
  classifyScannedCode,
  completeRunSheetCodes,
  isRunSheetText,
  normalizeLabelCode,
  parseEuro,
  parseRunSheetPart,
  parseSellerItemRef,
  runSheetEventKey,
  type PriceChangeReason,
  type RunSheetAssembly,
  type SellerItemRef,
} from '@barso/shared';
import { useEffect, useRef, useState, useSyncExternalStore, type SyntheticEvent } from 'react';
import { Link, useParams } from 'react-router';
import { messageFor } from '../api/messages';
import { HelpLink } from '../components/HelpLink';
import { CameraScanner, type RecentScan, type ScanFlash } from '../components/CameraScanner';
import { CustomerView } from '../components/CustomerView';
import { Icon } from '../components/icons';
import { DeviceCheck } from '../components/DeviceCheck';
import { Alert, Button, PageHeader } from '../components/ui';
import { saveFile } from '../download';
import { euro } from '../format';
import { useAutoSubmitCode } from '../hooks/useAutoSubmitCode';
import {
  cartTotal,
  promoSavings,
  confirmWarning,
  evaluateScan,
  NOT_RECEIVED_TAG,
  warningText,
} from '../pos/cart';
import {
  cardOffered as cardOfferedBy,
  noLabelAllowed as noLabelAllowedBy,
  priceChangeAllowed as priceChangeAllowedBy,
} from '../pos/payment';
import { importRunSheet, type RunSheetWarning } from '../pos/runSheetImport';
import { backAction, leaveRegister, useBackButton, useUnloadGuard } from '../pos/backButton';
import { correctableLines, paidCents, type Correction } from '../pos/correction';
import {
  applyPriceChange,
  clearPriceChange,
  keepPriceChange,
  reapplyPriceChange,
  type KeptPriceChange,
} from '../pos/priceChange';
import {
  removeAllLines,
  removeLine,
  removedGuard,
  restoreLines,
  undoText,
  type UndoEntry,
} from '../pos/cartUndo';
import {
  signalError,
  signalSoundOn,
  signalSuccess,
  signalTick,
  signalWarning,
  unlockAudio,
} from '../pos/feedback';
import {
  NO_PROMO_STATE,
  offeredPromo,
  priceLine,
  repriceCart,
  runningPromo,
  type PromoState,
  type PromoView,
} from '../pos/promo';
import type { IndexEntry } from '../pos/catalogIndex';
import { searchCatalog } from '../pos/catalogSearch';
import { standBarText, standHealth } from '../pos/standState';
import { forgetRecentPosEvent } from '../pos/sessions';
import { readSound, writeSound } from '../pos/sound';
import { ScanQueue, isQuietRepeat, type ScanSource } from '../pos/scanQueue';
import { MAX_PARKED } from '../pos/store';
import type { CartLine, LocalSale, ParkedCart } from '../pos/types';
import { useInvoices } from '../pos/useInvoices';
import { usePos, type PosHandle } from '../pos/usePos';
import { useWakeLock } from '../pos/wakeLock';
import {
  ItemSheet,
  LastSaleSheet,
  LeaveSheet,
  PromoBanner,
  ParkHintSheet,
  PaySheet,
  RefConfirmSheet,
  SalesSheet,
  Sheet,
  SyncDetails,
  UndoBar,
  syncView,
} from './posParts';
import { PosHeader, PosMenuSheet } from './posHeader';
import { ParkSheet } from './posParkSheet';
import { RunSheetCard, type RunSheetResultView } from './posRunSheet';
import { sendLabel, type SaleSend } from './posSaleCard';
import { SearchResults, SearchSheet, enterCaptures, useCatalogSearch } from './posSearchSheet';
import { ManualItemSheet, type ManualItemResult } from './posManualItemSheet';
import { NoteSheet, SaleNoteStatus, type NoteTarget } from './posNoteSheet';
import { PriceChangeSheet } from './posPriceSheet';
import { VoidConfirmSheet, type VoidAnswer } from './posVoidSheet';
import { ForeignRegister, HandoverSheet, StationSetup, TakeOver } from './posSetup';
import { InvoiceBox, InvoiceStatusBar } from './posInvoice';
import { CartRow, PaidSummary, RevokedAlert, WarningSheet } from './posRegisterParts';
import {
  clockMs,
  finePointer,
  lineText,
  refundCents,
  standTime,
  type Panel,
  type Stage,
  type VoidAsk,
  type Warning,
} from './posRegisterHelpers';

/** Kasse (Browser, POS-02 bis POS-08): offline nutzbar, Verkäufe werden zuerst lokal fest gespeichert, dann übertragen. */
export function PosPage() {
  const { eventId = '' } = useParams();
  const pos = usePos(eventId);
  // Bereit: Kassen-Modus mit eigener Kopfzeile, ohne Seitenkopf (das Layout zeigt hier keine Navigation)
  if (pos.phase.kind === 'ready') return <Register pos={pos} eventId={eventId} />;
  return (
    <div className="stack stack--lg cash-state">
      <PageHeader back={{ to: `/basare/${eventId}`, label: 'Basar' }} icon="euro" title="Kasse" />
      <p className="muted">
        <HelpLink slug="kasse-bedienen" />
      </p>
      {pos.phase.kind === 'loading' && <p role="status">Einen Moment bitte …</p>}
      {pos.phase.kind === 'locked' && (
        <Alert kind="error">
          Die Kasse dieses Basars ist in einem anderen Tab oder Fenster geöffnet. Bitte verwenden
          Sie nur eines: Sonst würden Verkaufsnummern doppelt vergeben.
        </Alert>
      )}
      {pos.phase.kind === 'failed' && <Alert kind="error">{pos.phase.message}</Alert>}
      {pos.phase.kind === 'setup' && <StationSetup pos={pos} eventId={eventId} />}
      {pos.phase.kind === 'handover' && (
        <TakeOver pos={pos} label={pos.register?.label ?? 'Die Kasse'} />
      )}
      {pos.phase.kind === 'foreign' && (
        <ForeignRegister
          pos={pos}
          waiting={pos.phase.waiting}
          label={pos.register?.label ?? 'Die Kasse'}
        />
      )}
    </div>
  );
}

/**
 * Kasse eines Geräts, das per Schnellzugang (ohne Konto, E11) an einen Kassenplatz gekoppelt ist (`/kasse-geraet/:eventId`).
 * Die Seite liegt bewusst außerhalb der Anmeldung: Es gibt kein Konto, nur das Geräte-Token im lokalen Speicher.
 */
export function DevicePosPage() {
  const { eventId = '' } = useParams();
  const pos = usePos(eventId);
  const missing = pos.phase.kind === 'setup';
  // Ohne lokale Kasse darf der Direktstart der App nicht wieder hierher führen (Sackgasse)
  useEffect(() => {
    if (missing) forgetRecentPosEvent(eventId);
  }, [missing, eventId]);
  if (pos.phase.kind === 'ready') {
    if (pos.deviceMode) return <Register pos={pos} eventId={eventId} />;
    return (
      <div className="stack stack--lg cash-state">
        <Alert kind="info">
          Diese Kasse wurde mit einem Konto geöffnet. Bitte dort mit Anmeldung weiterarbeiten.
        </Alert>
        <Link className="btn" to={`/basare/${eventId}/kasse`}>
          Zur Kasse
        </Link>
      </div>
    );
  }
  return (
    <div className="stack stack--lg cash-state">
      <h1>Kasse</h1>
      {pos.phase.kind === 'loading' && <p role="status">Einen Moment bitte …</p>}
      {pos.phase.kind === 'locked' && (
        <Alert kind="error">
          Die Kasse dieses Basars ist in einem anderen Tab oder Fenster geöffnet. Bitte verwenden
          Sie nur eines: Sonst würden Verkaufsnummern doppelt vergeben.
        </Alert>
      )}
      {pos.phase.kind === 'failed' && <Alert kind="error">{pos.phase.message}</Alert>}
      {pos.phase.kind === 'foreign' && (
        <Alert kind="info">
          Die Kasse auf diesem Gerät gehört einer anderen Person. Bitte mit Anmeldung öffnen.
        </Alert>
      )}
      {pos.phase.kind === 'setup' && (
        <Alert kind="info">
          Auf diesem Gerät ist noch keine Kasse gekoppelt. Scannen Sie den QR-Code Ihres
          Kassenplatzes oder den zentralen QR-Code „Kasse öffnen“.
        </Alert>
      )}
      <div className="row">
        <Link className="btn" to="/kasse-scannen">
          Kasse per QR koppeln
        </Link>
        <Link className="btn btn--secondary" to="/">
          Startseite
        </Link>
      </div>
    </div>
  );
}

/**
 * Kassen-Modus (POS-02 bis POS-08): Kopfzeile, Erfassen, Warenkorb (scrollt) und feste Fußleiste mit Summe und „Bezahlen“.
 * Alles Geld Betreffende liegt im Speicher; „bezahlt“ gilt erst nach `store.checkout`.
 */
function Register({ pos, eventId }: { pos: PosHandle; eventId: string }) {
  const store = pos.store;
  const [cart, setCart] = useState<CartLine[]>([]);
  // Spiegel des Warenkorbs: Aufträge der Warteschlange lesen immer den aktuellen Stand, nicht den ihres Renderns
  const cartRef = useRef<CartLine[]>([]);
  const [scan, setScan] = useState('');
  const [message, setMessage] = useState<{
    kind: 'success' | 'error' | 'info';
    text: string;
  } | null>(null);
  const [warning, setWarning] = useState<Warning | null>(null);
  const warningRef = useRef<Warning | null>(null);
  // Kamera-Codes, die bei offener Warnung oder offenem Bezahl-Blatt durchkamen: Sie werden danach wieder eingereiht
  const heldRef = useRef<string[]>([]);
  // Codes des eben kassierten Verkaufs: Solange „Bezahlt“ steht, übergeht die Kamera sie still
  const justSoldRef = useRef<Set<string>>(new Set());
  // Eingabe „17/5“ (O3): Pflicht-Bestätigung mit Bezeichnung und Preis, bevor die Zeile in den Warenkorb kommt
  const [refConfirm, setRefConfirm] = useState<{ ref: SellerItemRef; line: CartLine } | null>(null);
  const refConfirmRef = useRef<{ ref: SellerItemRef; line: CartLine } | null>(null);
  // Artikel liegt schon in einem geparkten Korb (X4): Wahl „trotzdem“ oder „Korb öffnen“
  const [parkHint, setParkHint] = useState<{ parkNo: number; line: CartLine } | null>(null);
  const parkHintRef = useRef<{ parkNo: number; line: CartLine } | null>(null);
  const [parked, setParked] = useState<ParkedCart[]>([]);
  const [nowMs, setNowMs] = useState(() => Date.now());
  // Rabattaktion (X7): Aktionen aus dem Snapshot, von Hand gestartete Aktion; der Spiegel gilt für Aufträge der Warteschlange
  const [promoState, setPromoState] = useState<PromoState>(NO_PROMO_STATE);
  const promoRef = useRef<PromoView | null>(null);
  // Codes, die gerade aus einem geparkten Korb zurückkommen: kein Hinweis „liegt in geparktem Korb“ dafür
  const unparkedRef = useRef<Set<string>>(new Set());
  // Begründete Preiskorrekturen (U17g) der Zeilen eines neu aufgebauten Korbs, je Artikel: bleiben beim erneuten Prüfen erhalten
  const keptPricesRef = useRef<Map<string, KeptPriceChange>>(new Map());
  // Entfernen mit „Rückgängig“ (K1): ein Platz, ein neues Entfernen ersetzt das vorige
  const [undo, setUndo] = useState<{ id: number; entry: UndoEntry } | null>(null);
  const undoRef = useRef<{ id: number; entry: UndoEntry } | null>(null);
  const undoId = useRef(0);
  // Entfernte Codes: Der Dauerscan nimmt ein noch im Bild liegendes Etikett nicht sofort wieder auf (`removedGuard`)
  const removedRef = useRef<Map<string, number>>(new Map());
  // Rückfrage vor dem Stornieren (Blatt statt window.confirm)
  const [voidAsk, setVoidAskState] = useState<VoidAsk | null>(null);
  const voidAskRef = useRef<VoidAsk | null>(null);
  // „Korrektur zu Nr. 42“ (K5): nur ein Hinweis fürs Bezahlen, gebucht wird nichts davon
  const [correction, setCorrectionState] = useState<Correction | null>(null);
  const correctionRef = useRef<Correction | null>(null);
  // Vermerke geparkter Korrektur-Körbe (nur im Speicher der Seite): Ohne ihn kassierte man nach dem Zurückholen die volle
  // Summe, ohne zu sehen, dass der Kunde schon bezahlt hat. `createdAtMs` stellt sicher, dass ein später unter derselben
  // Nummer geparkter Korb den Vermerk nie erbt.
  const parkedCorrections = useRef<Map<number, { createdAtMs: number; correction: Correction }>>(
    new Map(),
  );
  // Rückfrage „Kasse verlassen?“ (Zurück-Taste bei vollem Warenkorb)
  const [leaveAsk, setLeaveAskState] = useState(false);
  const leaveAskRef = useRef(false);
  const canGoBackRef = useRef(true);
  const [warningError, setWarningError] = useState<string | null>(null);
  const [manualPrice, setManualPrice] = useState('');
  const [stage, setStageState] = useState<Stage>('scan');
  const stageRef = useRef<Stage>('scan');
  /** Liest die Stufe frisch (nach einem `await` kann sie sich geändert haben; verhindert TS-Verengung auf alte Prüfungen). */
  const currentStage = (): Stage => stageRef.current;
  const [paid, setPaid] = useState<{
    total: number;
    change: number | null;
    card?: boolean;
    saleClientUuid?: string;
    registerSeq?: number;
  } | null>(null);
  // Zahlart „Karte“ (U14): nur wenn der zuletzt geladene Stand sie meldet (Einstellung des Basars und Server)
  const [cardOffered, setCardOffered] = useState(false);
  // Schalter des Basars (Paket F): ohne Meldung des Servers gilt erlaubt
  const [priceChangeOn, setPriceChangeOn] = useState(true);
  const [noLabelOn, setNoLabelOn] = useState(true);
  // Kaufbeleg (Welle 5d): nur wenn der zuletzt geladene Stand ihn meldet; Gerätekassen (ohne Konto) haben ihn nie
  const [invoiceOn, setInvoiceOn] = useState<boolean | null>(null);
  const recheckRef = useRef<string | null>(null);
  // Laufzettel (Zusatzfunktion): nur wenn der zuletzt geladene Stand ihn meldet; der Stand liegt auf dem Gerät, auch offline
  const [runSheetOn, setRunSheetOn] = useState(false);
  const runSheetOnRef = useRef(false);
  // Gelesene Teile des offenen Laufzettels (eine Kennung) und das Ergebnis der letzten Übernahme
  const [sheet, setSheet] = useState<RunSheetAssembly | null>(null);
  const sheetRef = useRef<RunSheetAssembly | null>(null);
  const [sheetResult, setSheetResult] = useState<RunSheetResultView | null>(null);
  // Schon übernommene Teile je Kennung (nur Speicher der Seite): derselbe Teil wird nie ein zweites Mal gezählt
  const usedSheetsRef = useRef<Map<string, Set<number>>>(new Map());
  // Rückfragen aus der Übernahme eines Laufzettels, die nacheinander gestellt werden („Hinweise bestätigen wie gewohnt“)
  const sheetWarningsRef = useRef<RunSheetWarning[]>([]);
  // Letzte Fehlermeldung je Art aus der Kamera: dasselbe Bild liefert sie sonst im Takt der Erkennung (Fehlerton gedrosselt)
  const sheetNoticeAtRef = useRef<Map<string, number>>(new Map());
  const [payError, setPayError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const payLock = useRef(false);
  const payBackRef = useRef<(() => boolean) | null>(null);
  // Ton an/aus (K8): je Gerät gemerkt, Standard an
  const [sound, setSoundState] = useState(readSound);
  // Die Kamera läuft an der Kasse immer (Dauerscan, U17a). Startet sie nicht, steht statt des Bildes das Codefeld mit Grund.
  const [cameraOn, setCameraOn] = useState(true);
  const [panel, setPanel] = useState<Panel>(null);
  // Blatt „Artikel“ zu einer Warenkorbzeile (Code); schließt sich, wenn die Zeile nicht mehr im Warenkorb liegt
  const [itemCode, setItemCode] = useState<string | null>(null);
  // Blatt „Artikel ohne Etikett erfassen“ (U17h)
  const [manualItemOpen, setManualItemOpen] = useState(false);
  // Kommentar an die Leitung (U17f): worauf er sich bezieht; `null` = Blatt zu
  const [noteTarget, setNoteTarget] = useState<NoteTarget | null>(null);
  // Zeile, deren Preis gerade korrigiert wird (U17g)
  const [priceCode, setPriceCode] = useState<string | null>(null);
  // Kundenansicht (K7): reine Anzeige, nichts wird gespeichert; gedreht, weil das Handy zwischen Kasse und Kunde liegt
  const [customerOpen, setCustomerOpen] = useState(false);
  const [customerRotated, setCustomerRotated] = useState(true);
  const [flash, setFlash] = useState<ScanFlash | null>(null);
  // Rückmeldung für Screenreader nach „In den Warenkorb“ (das Blatt ist dann schon zu)
  const [announce, setAnnounce] = useState('');
  const [recent, setRecent] = useState<RecentScan[]>([]);
  const [highlight, setHighlight] = useState<string | null>(null);
  const [queue] = useState(() => new ScanQueue());
  const recentId = useRef(0);
  const timers = useRef<{
    flash?: ReturnType<typeof setTimeout>;
    highlight?: ReturnType<typeof setTimeout>;
  }>({});
  const input = useRef<HTMLInputElement>(null);
  const focusRef = useRef<HTMLInputElement>(null);
  // Ohne Maus: keine Bildschirmtastatur, bis das Feld angetippt wird (der Fokus bleibt für den Handscanner im Feld)
  const [fine] = useState(finePointer);
  const [typing, setTyping] = useState(false);

  useWakeLock(true);

  // Browser geben Audio erst nach einer Geste frei: der erste Tipp (oder Tastendruck) entsperrt den Ton (K8). Ein Scan mit
  // der Kamera ist keine Geste und blieb sonst stumm.
  useEffect(() => {
    const unlock = () => {
      unlockAudio();
    };
    document.addEventListener('pointerdown', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
    return () => {
      document.removeEventListener('pointerdown', unlock);
      document.removeEventListener('keydown', unlock);
    };
  }, []);

  // Die Rückmeldung nach „In den Warenkorb“ steht nur kurz
  useEffect(() => {
    if (announce === '') return;
    const timer = setTimeout(() => {
      setAnnounce('');
    }, 4_000);
    return () => {
      clearTimeout(timer);
    };
  }, [announce]);

  // Die Markierung „alt“ der geparkten Körbe rechnet sich mit der Zeit neu
  useEffect(() => {
    const timer = setInterval(() => {
      setNowMs(Date.now());
    }, 60_000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  // Geparkte Körbe (X4) liegen in IndexedDB: nach Neuladen sind sie noch da
  useEffect(() => {
    if (store === null) return;
    const guard = { cancelled: false };
    void store.parkedList().then((list) => {
      if (guard.cancelled) return;
      setParked(list);
      setNowMs(Date.now());
    });
    return () => {
      guard.cancelled = true;
    };
  }, [store, pos.version]);

  // Code nach „Stand aktualisieren“ erneut prüfen, sobald der Abgleich durch ist
  useEffect(() => {
    const code = recheckRef.current;
    if (code === null || pos.status?.state === 'syncing') return;
    recheckRef.current = null;
    void queue.push(() => process(code, 'manual'));
  });
  // Fähigkeiten des Servers liegen im Store `meta`: nach Neuladen und nach jedem Sync neu lesen (Zahlart Karte, U14)
  useEffect(() => {
    if (store === null) return;
    const guard = { cancelled: false };
    void store.capabilities().then((capabilities) => {
      if (guard.cancelled) return;
      setCardOffered(cardOfferedBy(capabilities));
      setPriceChangeOn(priceChangeAllowedBy(capabilities));
      setNoLabelOn(noLabelAllowedBy(capabilities));
      // Schaltet der Basar eine Funktion aus, schließt sich ihre offene Maske (Paket F)
      if (!priceChangeAllowedBy(capabilities)) setPriceCode(null);
      if (!noLabelAllowedBy(capabilities)) setManualItemOpen(false);
      runSheetOnRef.current = capabilities.runSheet === true;
      setRunSheetOn(capabilities.runSheet === true);
      setInvoiceOn(capabilities.invoice === true);
    });
    return () => {
      guard.cancelled = true;
    };
  }, [store, pos.version]);

  // Aktionen (X7) liegen im Store `meta` (kein Verkauf): nach Neuladen und nach jedem Sync neu lesen
  useEffect(() => {
    if (store === null) return;
    const guard = { cancelled: false };
    void store.promoState().then((state) => {
      if (!guard.cancelled) setPromoState(state);
    });
    return () => {
      guard.cancelled = true;
    };
  }, [store, pos.version]);

  const running = runningPromo(promoState.promos, promoState.capable, promoState.activeId, nowMs);
  const offered =
    running === null ? offeredPromo(promoState.promos, promoState.capable, nowMs) : null;
  useEffect(() => {
    promoRef.current = running;
  }, [running]);

  // Die Automatik darf das Feld immer fokussieren: Ohne Maus steht es auf `inputMode="none"` (keine Tastatur)
  useEffect(() => {
    focusRef.current = input.current;
  }, []);
  useEffect(() => {
    const pending = timers.current;
    return () => {
      clearTimeout(pending.flash);
      clearTimeout(pending.highlight);
    };
  }, []);

  // Der Startwert der Prüfziffer ist im Browser nicht bekannt: die Prüfung übernimmt unverändert `evaluateScan`, die
  // Automatik löst nur statt des Klicks aus.
  const autoCapture = useAutoSubmitCode({
    value: scan,
    clear: () => {
      setScan('');
    },
    onCode: (code) => {
      enqueue(code, 'manual');
    },
    inputRef: focusRef,
    enabled: stage !== 'pay' && warning === null && panel === null,
  });

  // Ohne Kamerabild ist das Codefeld zugleich Suchfeld: Treffer stehen direkt darunter (U17b)
  // Artikelstand (U17c): geladene Artikel wachsen Seite für Seite mit dem Index
  const loadedItems = useSyncExternalStore(
    (listener) => pos.catalog.subscribe(listener),
    () => pos.catalog.size,
  );
  const standInfo = standHealth({
    stand: pos.stand,
    nowMs,
    connection: pos.connection,
    syncing: pos.status?.state === 'syncing',
    lastSuccessAtMs: pos.status?.lastSuccessAt?.getTime() ?? null,
    loaded: loadedItems,
  });
  const standBar = standBarText(standInfo);
  const inlineSearch = useCatalogSearch(pos.catalog, cameraOn ? '' : scan);
  // Kaufbeleg per E-Mail (Welle 5d): wartende Anfragen gehen nach dem Abgleich des Verkaufs hinaus, nur mit dem Konto der Kasse
  const invoices = useInvoices({
    store,
    enabled: pos.deviceMode ? false : invoiceOn,
    revoked: pos.revoked,
    connection: pos.connection,
    version: pos.version,
    eventId,
    registerId: pos.register?.registerId,
  });
  const total = cartTotal(cart);
  const saved = promoSavings(cart);
  const itemLine = itemCode === null ? undefined : cart.find((entry) => entry.code === itemCode);
  const priceTarget =
    priceCode === null ? undefined : cart.find((entry) => entry.code === priceCode);
  const modal =
    warning !== null ||
    refConfirm !== null ||
    parkHint !== null ||
    leaveAsk ||
    voidAsk !== null ||
    stage === 'pay' ||
    itemLine !== undefined ||
    priceTarget !== undefined ||
    noteTarget !== null ||
    manualItemOpen ||
    customerOpen ||
    panel !== null;
  // Handscanner (HID) auch dann, wenn der Fokus gerade woanders liegt (z. B. nach einem Blatt auf dessen Auslöser): Ein
  // Zeichen ohne Blatt und außerhalb eines Eingabefelds geht ins Codefeld, und der Fokus bleibt dort für den Rest des Codes
  useEffect(() => {
    if (modal) return;
    const redirect = (event: KeyboardEvent) => {
      const field = input.current;
      if (field === null || event.defaultPrevented) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key.length !== 1 || event.key === ' ') return;
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.isContentEditable || target.closest('input, textarea, select') !== null)
      ) {
        return;
      }
      event.preventDefault();
      field.focus({ preventScroll: true });
      setScan((current) => current + event.key);
    };
    document.addEventListener('keydown', redirect);
    return () => {
      document.removeEventListener('keydown', redirect);
    };
  }, [modal]);
  const view = syncView(pos);
  const lastSale = pos.history[0];
  // Sendestatus der Verkäufe (U17e): Verbindung aus echten Anfragen, Fehler des letzten Laufs, „Jetzt senden“
  const send: SaleSend = {
    connection: pos.connection,
    syncError: pos.status?.state === 'error',
    openVoids: pos.openVoids,
    onSendNow: pos.syncNow,
  };

  function commitCart(next: CartLine[]) {
    cartRef.current = next;
    setCart(next);
    // Ist die Zeile des offenen Artikel-Blatts weg (anderer Korb), schließt sich das Blatt
    setItemCode((open) => (open !== null && next.some((line) => line.code === open) ? open : null));
    setPriceCode((open) =>
      open !== null && next.some((line) => line.code === open) ? open : null,
    );
  }
  function go(next: Stage) {
    stageRef.current = next;
    setStageState(next);
  }
  function chooseWarning(next: Warning | null) {
    warningRef.current = next;
    setWarning(next);
  }
  function chooseRefConfirm(next: { ref: SellerItemRef; line: CartLine } | null) {
    refConfirmRef.current = next;
    setRefConfirm(next);
  }
  function chooseUndo(next: { id: number; entry: UndoEntry } | null) {
    undoRef.current = next;
    setUndo(next);
  }
  /** Die Leiste weicht jedem anderen Vorgang (Bezahlen, Parken, Zurückholen, Aktion): Rückgängig fiele sonst in einen anderen Korb. */
  function clearUndo() {
    if (undoRef.current !== null) chooseUndo(null);
  }
  function setCorrection(next: Correction | null) {
    correctionRef.current = next;
    setCorrectionState(next);
  }
  function chooseVoidAsk(next: VoidAsk | null) {
    voidAskRef.current = next;
    setVoidAskState(next);
  }
  function chooseLeaveAsk(next: boolean) {
    leaveAskRef.current = next;
    setLeaveAskState(next);
  }
  function chooseParkHint(next: { parkNo: number; line: CartLine } | null) {
    parkHintRef.current = next;
    setParkHint(next);
  }
  /** Offene Rückfrage (Warnung, Nummern-Bestätigung, Hinweis auf geparkten Korb): Neues wartet, bis sie beantwortet ist. */
  const questionOpen = (): boolean =>
    warningRef.current !== null ||
    refConfirmRef.current !== null ||
    parkHintRef.current !== null ||
    leaveAskRef.current ||
    voidAskRef.current !== null;
  function toggleSound() {
    const next = !sound;
    writeSound(next);
    setSoundState(next);
    // Beim Einschalten hört man sofort, dass es geht
    if (next) signalSoundOn();
  }
  /** Fokus zurück ins Codefeld (Handscanner). Ohne Maus öffnet das keine Tastatur (`inputMode="none"`, bis man antippt). */
  function focusField() {
    input.current?.focus({ preventScroll: true });
  }

  function signal(kind: ScanFlash, text: string) {
    setFlash(kind);
    clearTimeout(timers.current.flash);
    timers.current.flash = setTimeout(() => {
      setFlash(null);
    }, 300);
    recentId.current += 1;
    const entry: RecentScan = { id: recentId.current, kind, text };
    setRecent((list) => [entry, ...list].slice(0, 3));
  }

  function enqueue(raw: string, source: ScanSource) {
    void queue.push(() => process(raw, source));
  }

  /** Merkt einen Kamera-Code für später (ohne Doppelte); er geht nicht verloren, weil die Kamera ihn nicht wiederholt. */
  function hold(raw: string) {
    if (!heldRef.current.includes(raw)) heldRef.current.push(raw);
  }

  /** Reiht die gemerkten Codes wieder ein; schon im Warenkorb Liegendes bleibt dabei still (Kamera-Regel). */
  function releaseHeld(skipCode?: string) {
    const codes = heldRef.current;
    heldRef.current = [];
    for (const raw of codes) {
      // Der Code der eben beantworteten Warnung käme sonst als dieselbe Warnung zurück
      if (skipCode !== undefined && classifyScannedCode(raw)?.code === skipCode) continue;
      enqueue(raw, 'camera');
    }
  }

  /** Liegt der Artikel dieses Treffers schon im Warenkorb? */
  const inCart = (entry: IndexEntry): boolean =>
    cart.some((line) => line.itemId === entry.id || line.code === entry.code);

  /** „In den Warenkorb“ aus der Suche: derselbe Weg wie ein Code von Hand (Prüfungen, Warnungen, Rückfragen). */
  function addFromSearch(entry: IndexEntry) {
    setPanel(null);
    setAnnounce(`${entry.title} ausgewählt`);
    enqueue(entry.code, 'manual');
  }

  function handleScan(submit: SyntheticEvent) {
    submit.preventDefault();
    const raw = scan;
    // Ein Suchwort mit Treffern erfasst nichts: Man tippt auf einen Treffer (Codes und Nummern gehen wie immer)
    if (
      /\p{L}/u.test(raw) &&
      !isRunSheetText(raw) &&
      !enterCaptures(pos.catalog, raw) &&
      searchCatalog(pos.catalog.entries(), raw).total > 0
    ) {
      setMessage({ kind: 'info', text: 'Bitte einen Treffer antippen.' });
      return;
    }
    setScan('');
    enqueue(raw, 'manual');
  }

  function chooseSheet(next: RunSheetAssembly | null) {
    sheetRef.current = next;
    setSheet(next);
  }

  /** Text eines Laufzettel-QR: Teil lesen, prüfen und zum offenen Laufzettel fügen. Übernommen wird erst mit dem Knopf. */
  function handleRunSheet(raw: string, source: ScanSource) {
    const fail = (text: string, key = text) => {
      if (source === 'camera') {
        const now = Date.now();
        const last = sheetNoticeAtRef.current.get(key) ?? -Infinity;
        if (now - last < 4_000) return;
        sheetNoticeAtRef.current.set(key, now);
      }
      signalError();
      signal('error', `✗ ${text}`);
      setMessage({ kind: 'error', text });
      focusField();
    };
    if (questionOpen()) {
      // Wie bei einem Etikett: Die Kamera ist pausiert, was durchkam, wird gemerkt und nach der Antwort nachgeholt
      if (source === 'manual')
        setMessage({ kind: 'info', text: 'Bitte zuerst die Frage beantworten.' });
      else hold(raw);
      return;
    }
    if (!runSheetOnRef.current) {
      fail(
        'Der Laufzettel ist in diesem Basar ausgeschaltet. Bitte die Etiketten einzeln scannen.',
      );
      return;
    }
    const part = parseRunSheetPart(raw);
    if (part === null) {
      fail(
        'Der Laufzettel ist beschädigt oder nicht vollständig gelesen. Bitte den QR-Code noch einmal scannen.',
      );
      return;
    }
    if (part.eventKey !== runSheetEventKey(eventId)) {
      fail('Dieser Laufzettel gehört zu einem anderen Basar.');
      return;
    }
    if (
      usedSheetsRef.current.get(part.id)?.has(part.part) === true &&
      part.codes.some((code) => cartRef.current.some((line) => line.code === code))
    ) {
      // Schon übernommen und noch im Warenkorb: nie ein zweites Mal zählen. Liegt keiner der Codes mehr im Warenkorb (Artikel
      // entfernt oder schon kassiert), darf der Laufzettel erneut übernommen werden; `evaluateScan` schützt vor Doppelten.
      if (source === 'manual') {
        setMessage({ kind: 'info', text: 'Dieser Laufzettel ist schon übernommen.' });
      } else {
        const now = Date.now();
        if (now - (sheetNoticeAtRef.current.get('used') ?? -Infinity) >= 4_000) {
          sheetNoticeAtRef.current.set('used', now);
          setMessage({ kind: 'info', text: 'Dieser Laufzettel ist schon übernommen.' });
        }
      }
      return;
    }
    const added = addRunSheetPart(sheetRef.current, part);
    if (added.kind === 'other_sheet') {
      fail('Das ist ein anderer Laufzettel. Bitte zuerst den offenen übernehmen oder verwerfen.');
      return;
    }
    if (added.kind === 'inconsistent') {
      fail('Dieser Teil gehört nicht zum offenen Laufzettel. Bitte verwerfen und neu scannen.');
      return;
    }
    if (added.kind === 'repeat') {
      if (source === 'manual') {
        setMessage({
          kind: 'info',
          text: `Teil ${String(part.part)}/${String(part.parts)} ist schon gelesen.`,
        });
      }
      return;
    }
    if (stageRef.current === 'done') {
      // Der nächste Kunde fängt an: die Rückgeld-Anzeige weicht dem Warenkorb
      go('scan');
      setPaid(null);
    }
    chooseSheet(added.assembly);
    setSheetResult(null);
    signalSuccess();
    signal('ok', `Laufzettel Teil ${String(part.part)}/${String(part.parts)}`);
    setMessage(null);
    focusField();
  }

  /** Verwirft den offenen Laufzettel (er wurde nie in den Warenkorb übernommen). */
  function discardSheet() {
    chooseSheet(null);
    focusField();
  }

  /** Stellt die nächste Rückfrage aus der Übernahme (schon verkauft, gelöscht, unbekannt), sobald keine andere offen ist. */
  function nextSheetWarning() {
    if (warningRef.current !== null) return;
    for (;;) {
      const next = sheetWarningsRef.current.shift();
      if (next === undefined) return;
      // Derselbe Artikel über einen anderen Code liegt inzwischen im Warenkorb: keine zweite Zeile
      const inCartAlready = cartRef.current.some(
        (line) => line.code === next.code || (next.item !== null && line.itemId === next.item.id),
      );
      if (inCartAlready) continue;
      signalWarning();
      signal('warn', `! ${warningText(next.reason)}`);
      chooseWarning(next);
      setManualPrice('');
      setWarningError(null);
      setMessage(null);
      return;
    }
  }

  /**
   * Übernimmt den vollständigen Laufzettel in den Warenkorb. Jeder Code läuft durch dieselbe Prüfung wie ein Einzelscan
   * (`importRunSheet`): Was glatt geht, kommt in den Warenkorb, Hinweise stehen im Ergebnis, Rückfragen folgen nacheinander.
   */
  function importSheet() {
    void queue.push(async () => {
      const assembly = sheetRef.current;
      const codes = assembly === null ? null : completeRunSheetCodes(assembly);
      if (
        store === null ||
        assembly === null ||
        codes === null ||
        stageRef.current === 'pay' ||
        questionOpen()
      ) {
        return;
      }
      const register = await store.register();
      const found = new Map<string, Awaited<ReturnType<typeof store.findByCode>>>();
      for (const code of codes) found.set(code, await store.findByCode(code));
      const parkedCarts = await store.parkedList();
      // Das Bezahl-Blatt kann sich während des Wartens geöffnet haben: die Summe darf sich darunter nicht ändern
      if (currentStage() === 'pay') return;
      const result = importRunSheet(
        codes,
        register?.labelSalt ?? 0,
        (code) => found.get(code),
        cartRef.current,
        (line) =>
          parkedCarts.find((entry) =>
            entry.lines.some(
              (other) =>
                other.code === line.code || (line.itemId !== null && other.itemId === line.itemId),
            ),
          )?.parkNo,
      );
      const used = usedSheetsRef.current.get(assembly.id) ?? new Set<number>();
      for (const part of assembly.received.keys()) used.add(part);
      usedSheetsRef.current.set(assembly.id, used);
      if (usedSheetsRef.current.size > 50) {
        const oldest = usedSheetsRef.current.keys().next();
        if (oldest.done !== true) usedSheetsRef.current.delete(oldest.value);
      }
      if (currentStage() === 'done') {
        go('scan');
        setPaid(null);
      }
      clearUndo();
      const priced = result.lines.map((line) => {
        // Wie beim Einzelscan: Eine Zeile aus einem neu aufgebauten Korb behält ihre begründete Preiskorrektur (U17g)
        const kept = line.itemId === null ? undefined : keptPricesRef.current.get(line.itemId);
        if (line.itemId !== null) keptPricesRef.current.delete(line.itemId);
        return kept === undefined
          ? priceLine(line, promoRef.current)
          : reapplyPriceChange(line, kept);
      });
      const before = cartRef.current.length;
      if (priced.length > 0) commitCart([...cartRef.current, ...priced]);
      chooseSheet(null);
      setSheetResult({
        total: result.total,
        added: priced.length,
        hints: result.hints,
        alreadyInCart: before,
      });
      sheetWarningsRef.current = [...result.warnings];
      if (result.hints.length === 0) signalSuccess();
      else signalWarning();
      setMessage(null);
      nextSheetWarning();
      focusField();
    });
  }

  /** Ein erfasster Code, gleich woher (Tastatur, Handscanner, Kamera). Läuft nur über die Warteschlange, nie gleichzeitig. */
  async function process(raw: string, source: ScanSource) {
    if (store === null || raw.trim() === '' || stageRef.current === 'pay') return;
    // Text eines Laufzettel-QR (Kamera oder Handscanner-Feld): gehört nie in die Etikettenprüfung
    if (isRunSheetText(raw)) {
      handleRunSheet(raw, source);
      return;
    }
    // Eingabe „17/5“ (nur von Hand): Verkäufer- und Artikelnummer, der Trenner ist Pflicht (O3). Ein fremder Code kann
    // genauso aussehen (z. B. „17-52“): Zuerst zählt der Code. Nur wenn er keinen Artikel trifft, gilt die Eingabe als Nummer.
    const ref = source === 'manual' ? parseSellerItemRef(raw) : null;
    const codeScan = classifyScannedCode(raw);
    const codeItem = codeScan === null ? undefined : await store.findByCode(codeScan.code);
    const refItem =
      ref === null || codeItem !== undefined
        ? undefined
        : await store.findBySellerItem(ref.sellerNumber, ref.itemNo);
    // BARSO-Code normalisiert, ein übernommener fremder Code in einheitlicher Schreibweise
    const scanned = classifyScannedCode(refItem?.code ?? raw);
    const item =
      refItem ??
      codeItem ??
      (codeScan === null ? await store.findByCode(normalizeLabelCode(raw)) : undefined);
    // Die Kamera sieht oft auch Herstellercodes (EAN der Ware): unbekannte fremde Codes still übergehen, bevor sich an
    // Anzeige oder Warnung etwas ändert
    if (source === 'camera' && scanned?.kind === 'external' && item === undefined) return;
    // Eben entfernter Artikel, dessen Etikett noch im Kamerabild liegt: still übergehen (Eingabe von Hand geht immer)
    if (
      source === 'camera' &&
      removedGuard(removedRef.current, item?.code ?? scanned?.code ?? '', clockMs())
    ) {
      return;
    }
    if (questionOpen()) {
      // Die Kamera ist dann pausiert; was noch durchkommt, wird gemerkt und nach der Antwort nachgeholt (`releaseHeld`).
      // Eingaben von Hand bekommen den Hinweis.
      if (source === 'manual') {
        setMessage({ kind: 'info', text: 'Bitte zuerst die Frage beantworten.' });
      } else {
        hold(raw);
      }
      return;
    }
    // Während „Bezahlt“ steht, sind die eben kassierten Etiketten noch im Bild: still übergehen
    if (
      source === 'camera' &&
      stageRef.current === 'done' &&
      justSoldRef.current.has(item?.code ?? scanned?.code ?? '')
    ) {
      return;
    }
    const register = await store.register();
    // Das Bezahl-Blatt kann sich während des Wartens geöffnet haben: die Summe darf sich darunter nicht ändern
    if (currentStage() === 'pay') {
      if (source === 'camera') hold(raw);
      return;
    }
    // Nummer ohne Treffer (und kein Code mit diesem Aussehen, der einen Artikel hätte): nie einen Code erfinden
    if (ref !== null && item === undefined) {
      const text = `Kein Artikel ${String(ref.sellerNumber)}/${String(ref.itemNo)} im Stand dieser Kasse.`;
      signalError();
      signal('error', `✗ ${text}`);
      setMessage({
        kind: 'error',
        text: `${text} Bitte den Code vom Etikett eingeben oder die Nummer prüfen.`,
      });
      focusField();
      return;
    }
    const outcome = evaluateScan(
      refItem?.code ?? raw,
      register?.labelSalt ?? 0,
      item,
      cartRef.current,
    );
    if (isQuietRepeat(outcome, source)) {
      // Liegendes oder erneut vorgehaltenes Etikett: kein Fehlerton, die Zeile blitzt kurz auf
      const line = cartRef.current.find(
        (entry) => entry.code === scanned?.code || (item !== undefined && entry.itemId === item.id),
      );
      if (line !== undefined) {
        setHighlight(line.code);
        clearTimeout(timers.current.highlight);
        timers.current.highlight = setTimeout(() => {
          setHighlight(null);
        }, 1000);
      }
      return;
    }
    if (stageRef.current === 'done') {
      // Der nächste Kunde fängt an: die Rückgeld-Anzeige weicht dem Warenkorb (die Zeile „Letzter Kunde“ bleibt)
      go('scan');
      setPaid(null);
    }
    if (outcome.kind === 'added') {
      // Läuft eine Aktion (X7), bekommt ein Artikel mit Einverständnis den Aktionspreis (sonst bleibt der volle Preis)
      // Eine Zeile aus einem neu aufgebauten Korb behält ihre begründete Preiskorrektur (U17g), sonst gilt die Aktion
      const itemId = outcome.line.itemId;
      const kept = itemId === null ? undefined : keptPricesRef.current.get(itemId);
      if (itemId !== null) keptPricesRef.current.delete(itemId);
      const priced =
        kept === undefined
          ? priceLine(outcome.line, promoRef.current)
          : reapplyPriceChange(outcome.line, kept);
      if (ref !== null && refItem !== undefined) {
        // Pflicht-Bestätigung mit Bezeichnung und Preis (Tippfehler bei der Nummer); die Kamera pausiert dabei
        chooseRefConfirm({ ref, line: priced });
        setMessage(null);
        return;
      }
      await admitLine(priced);
    } else if (outcome.kind === 'warning') {
      signalWarning();
      signal('warn', `! ${warningText(outcome.reason)}`);
      chooseWarning(outcome);
      setManualPrice('');
      setWarningError(null);
      setMessage(null);
    } else {
      signalError();
      signal('error', `✗ ${outcome.message}`);
      setMessage({ kind: 'error', text: outcome.message });
    }
    focusField();
  }

  /**
   * Nimmt eine geprüfte Zeile in den Warenkorb auf. Liegt der Artikel schon in einem geparkten Korb, fragt die Kasse
   * erst nach (X4): Sonst könnte derselbe Artikel zweimal kassiert werden.
   */
  async function admitLine(line: CartLine) {
    if (store === null) return;
    const inParked = (await store.parkedList()).find((entry) =>
      entry.lines.some(
        (other) =>
          other.code === line.code || (line.itemId !== null && other.itemId === line.itemId),
      ),
    );
    // Das Bezahl-Blatt kann sich während des Wartens geöffnet haben: die Summe darf sich darunter nicht ändern
    if (currentStage() === 'pay') {
      hold(line.code);
      return;
    }
    const returning = unparkedRef.current.delete(line.code);
    if (inParked !== undefined && !returning) {
      signalWarning();
      signal('warn', `! Liegt in geparktem Korb P${String(inParked.parkNo)}`);
      chooseParkHint({ parkNo: inParked.parkNo, line });
      setMessage(null);
      return;
    }
    addLine(line);
  }

  function addLine(line: CartLine) {
    if (cartRef.current.some((entry) => entry.code === line.code)) return;
    if (line.override === 'not_received') {
      // Hinweis ohne Rückfrage (U17e): kurzer Warnton, Marke im Bild, die Kamera läuft weiter
      signalWarning();
      signal('warn', `! ${NOT_RECEIVED_TAG}: ${lineText(line).slice(2)}`);
    } else {
      signalSuccess();
      signal('ok', lineText(line));
    }
    commitCart([...cartRef.current, line]);
    setMessage(null);
  }

  /**
   * Artikel ohne Etikett in den Warenkorb (U17h). Das Foto wird zuerst gespeichert; gelingt das nicht (Speicher voll), geht der
   * Artikel trotzdem hinein, mit dem Hinweis. Läuft in der Scan-Warteschlange, nie unter dem offenen Bezahl-Blatt.
   */
  function addManualItem(result: ManualItemResult) {
    setManualItemOpen(false);
    void queue.push(async () => {
      if (store === null || stageRef.current === 'pay') return;
      let line = result.line;
      let photoNote: string | null = null;
      if (result.photo !== null) {
        const photoId = crypto.randomUUID();
        try {
          await store.addPhoto(photoId, result.photo);
          line = { ...line, manual: { ...line.manual, photoId } };
        } catch (caught) {
          photoNote = caught instanceof Error ? caught.message : 'Foto nicht gespeichert.';
        }
      }
      if (cartRef.current.some((entry) => entry.code === line.code)) return;
      clearUndo();
      signalSuccess();
      signal('ok', lineText(line));
      commitCart([...cartRef.current, line]);
      setMessage(photoNote === null ? null : { kind: 'info', text: photoNote });
      focusField();
    });
  }

  function confirm() {
    if (warning === null) return;
    // Freier Betrag zu unbekanntem Code nur, wenn der Basar Verkauf ohne Etikett erlaubt (Paket F)
    if (warning.item === null && !noLabelOn) {
      setWarningError('Freie Beträge sind für diesen Basar ausgeschaltet.');
      return;
    }
    const confirmed = confirmWarning(
      warning,
      warning.item === null ? (parseEuro(manualPrice) ?? undefined) : undefined,
    );
    if (confirmed === null) {
      setWarningError('Bitte geben Sie den Preis in Euro an (0,50 bis 5.000).');
      return;
    }
    const line = priceLine(confirmed, promoRef.current);
    commitCart([...cartRef.current, line]);
    signal('ok', lineText(line));
    chooseWarning(null);
    unparkedRef.current.delete(warning.code);
    setMessage(null);
    focusField();
    nextSheetWarning();
    releaseHeld(warning.code);
  }

  /** Antwort auf die Nummern-Bestätigung (O3): „Ja, kassieren“ nimmt die Zeile auf, sonst verwirft sie. */
  function answerRef(yes: boolean) {
    const answered = refConfirmRef.current;
    chooseRefConfirm(null);
    // Über die Warteschlange: erst die Zeile aufnehmen, dann das Gemerkte nachholen (sonst öffnen sich zwei Blätter)
    if (yes && answered !== null) {
      // Die Aktion kann sich während der Rückfrage geändert haben: Preis neu anwenden
      const line = priceLine(answered.line, promoRef.current);
      void queue.push(() => admitLine(line));
    }
    focusField();
    releaseHeld(answered?.line.code);
  }

  function answerParkHint(choice: 'anyway' | 'open' | 'cancel') {
    const answered = parkHintRef.current;
    chooseParkHint(null);
    if (answered !== null && choice === 'anyway') {
      addLine(priceLine(answered.line, promoRef.current));
    }
    if (answered !== null && choice === 'open') requestUnpark(answered.parkNo);
    focusField();
    releaseHeld(answered?.line.code);
  }

  /**
   * Entfernt eine Zeile oder (ohne Code) den ganzen Warenkorb und hält den Rückgängig-Platz bereit. Läuft in der
   * Scan-Warteschlange: ein gleichzeitig eintreffender Scan sieht nie einen halb geänderten Korb. Nie unter dem
   * offenen Bezahl-Blatt (die Summe darf sich darunter nicht ändern).
   */
  function requestRemove(code?: string) {
    void queue.push(async () => {
      if (stageRef.current === 'pay') return;
      const removed =
        code === undefined ? removeAllLines(cartRef.current) : removeLine(cartRef.current, code);
      if (removed === null) return;
      commitCart(removed.cart);
      // Ein leerer Korb gehört zu keinem Verkauf mehr: der Hinweis „Korrektur“ würde sonst am nächsten Kunden hängen
      if (removed.cart.length === 0) setCorrection(null);
      const now = Date.now();
      for (const entry of removed.undo.removed) removedRef.current.set(entry.line.code, now);
      undoId.current += 1;
      chooseUndo({ id: undoId.current, entry: removed.undo });
      setMessage(null);
      await Promise.resolve();
    });
  }
  /**
   * Preis einer Zeile korrigieren (U17g) oder zurücknehmen. Läuft in der Scan-Warteschlange (ein gleichzeitig eintreffender
   * Scan sieht nie halbe Preise) und nie unter dem offenen Bezahl-Blatt. Ohne Grund gilt kein neuer Preis (`applyPriceChange`).
   */
  function requestPriceChange(
    code: string,
    change: { cents: number; reason: PriceChangeReason; note: string | undefined } | null,
  ) {
    void queue.push(async () => {
      if (stageRef.current === 'pay') return;
      const next = cartRef.current.map((line) => {
        if (line.code !== code) return line;
        // Zurücknehmen rechnet nach der JETZT laufenden Aktion (X7): Endete sie inzwischen, gilt wieder der Listenpreis
        const updated =
          change === null
            ? priceLine(clearPriceChange(line), promoRef.current)
            : applyPriceChange(line, change.cents, change.reason, change.note);
        return updated ?? line;
      });
      if (!next.some((line, index) => line !== cartRef.current[index])) return;
      clearUndo();
      commitCart(next);
      setMessage({
        kind: 'info',
        text: change === null ? 'Korrektur zurückgenommen.' : 'Preis korrigiert.',
      });
      await Promise.resolve();
    });
  }
  /** „Rückgängig“: Zeilen an den alten Platz, Preis neu nach der jetzt laufenden Aktion (X7), nie doppelt. */
  function requestUndo() {
    void queue.push(async () => {
      const current = undoRef.current;
      if (current === null || stageRef.current === 'pay' || questionOpen()) return;
      signalTick();
      const result = restoreLines(cartRef.current, current.entry, promoRef.current);
      for (const entry of current.entry.removed) removedRef.current.delete(entry.line.code);
      chooseUndo(null);
      commitCart(result.cart);
      if (result.restored < current.entry.removed.length) {
        setMessage({ kind: 'info', text: 'Ein Artikel liegt schon wieder im Warenkorb.' });
      }
      await Promise.resolve();
    });
  }

  /** Parkt den Warenkorb (X4): rein lokal, kein Verkauf, keine Nummer, kein Sync. */
  function requestPark() {
    void queue.push(parkNow);
  }
  function requestUnpark(parkNo: number) {
    void queue.push(() => unparkNow(parkNo));
  }
  // Beides läuft in der Scan-Warteschlange: ein gleichzeitig eintreffender Scan sieht nie einen halb getauschten Korb
  async function parkNow() {
    const lines = cartRef.current;
    if (store === null || lines.length === 0 || questionOpen() || stageRef.current === 'pay')
      return;
    try {
      const saved = await store.park(lines);
      // Der Vermerk „Korrektur“ geht mit dem Korb in den Parkplatz und kommt beim Zurückholen wieder
      if (correctionRef.current !== null) {
        parkedCorrections.current.set(saved.parkNo, {
          createdAtMs: saved.createdAtMs,
          correction: correctionRef.current,
        });
      }
      commitCart([]);
      setCorrection(null);
      clearUndo();
      removedRef.current = new Map();
      setMessage({ kind: 'info', text: `Warenkorb als P${String(saved.parkNo)} geparkt.` });
      pos.refresh();
    } catch (caught) {
      setMessage({ kind: 'error', text: messageFor(caught) });
    }
  }

  /**
   * Holt einen geparkten Korb zurück (ein aktueller Warenkorb nimmt dessen Platz ein). Jede Zeile wird neu bewertet: Was
   * inzwischen anderswo verkauft wurde, kommt mit der üblichen Warnung, Preise kommen aus dem aktuellen Stand.
   */
  async function unparkNow(parkNo: number) {
    if (store === null || questionOpen() || stageRef.current === 'pay') return;
    try {
      const current = correctionRef.current;
      const swapped = cartRef.current.length > 0;
      const back = await store.unpark(parkNo, cartRef.current);
      const kept = parkedCorrections.current.get(parkNo);
      parkedCorrections.current.delete(parkNo);
      // Der bisherige Korb tritt an den Platz des geholten: sein Vermerk geht mit (Zeitstempel aus dem Speicher)
      if (swapped && current !== null) {
        const slot = (await store.parkedList()).find((entry) => entry.parkNo === parkNo);
        if (slot !== undefined) {
          parkedCorrections.current.set(parkNo, {
            createdAtMs: slot.createdAtMs,
            correction: current,
          });
        }
      }
      setCorrection(kept?.createdAtMs === back.createdAtMs ? kept.correction : null);
      await reloadLines(back.lines);
      pos.refresh();
    } catch (caught) {
      setMessage({ kind: 'error', text: messageFor(caught) });
    }
  }

  /**
   * Baut den Warenkorb aus gespeicherten Zeilen neu auf (Zurückholen eines geparkten Korbs, Korrigieren eines Verkaufs).
   * Gemeinsamer Weg, damit beides gleich bewertet: Von Hand genannte Preise unbekannter Artikel bleiben; alles andere läuft
   * wie ein Scan durch die Prüfung, der Preis kommt frisch aus dem Stand und der laufenden Aktion; nur eine begründete
   * Preiskorrektur (U17g) bleibt mit Preis, Grund und Text erhalten. Zeilen ohne Artikel (offline als unbekannt erfasst) werden ebenfalls neu geprüft: Kennt der Stand den Code
   * inzwischen, läuft die Zeile durch die Bewertung, sonst bleibt der genannte Preis. Läuft in der Scan-Warteschlange.
   */
  async function reloadLines(lines: readonly CartLine[]) {
    if (store === null) return;
    clearUndo();
    removedRef.current = new Map();
    justSoldRef.current = new Set();
    unparkedRef.current = new Set();
    keptPricesRef.current = new Map();
    setPaid(null);
    go('scan');
    const keep: CartLine[] = [];
    const recheck: CartLine[] = [];
    for (const line of lines) {
      if (line.itemId !== null || (await store.findByCode(line.code)) !== undefined) {
        recheck.push(line);
      } else {
        keep.push(line);
      }
    }
    commitCart(keep);
    for (const line of recheck) {
      unparkedRef.current.add(line.code);
      const kept = keepPriceChange(line);
      if (kept !== undefined && line.itemId !== null) keptPricesRef.current.set(line.itemId, kept);
      enqueue(line.code, 'camera');
    }
    setMessage(null);
  }

  /**
   * Korrigieren (K5): Der Verkauf wird mit dem bisherigen `voidSale` storniert (ein Void-Eintrag, kein neues `seq`), danach
   * kommen seine noch gültigen Zeilen über `reloadLines` in den Warenkorb. Neu kassiert wird wie immer mit `checkout`.
   * Reihenfolge fest: erst Storno, dann Neuaufbau. Bricht etwas dazwischen ab, ist der Storno gespeichert und der Korb nur
   * Zustand: Es geht kein Geld verloren, schlimmstenfalls muss neu gescannt werden.
   */
  async function correctNow(sale: LocalSale) {
    if (store === null) return;
    if (cartRef.current.length > 0 || stageRef.current === 'pay' || questionOpen()) {
      setMessage({
        kind: 'info',
        text: 'Bitte den aktuellen Warenkorb erst parken oder kassieren.',
      });
      return;
    }
    try {
      // Frischer Stand aus dem Speicher: eine inzwischen einzeln stornierte Position kommt nicht zurück
      const fresh = (await store.history(5000)).find(
        (entry) => entry.clientUuid === sale.clientUuid,
      );
      if (fresh === undefined) throw new Error('Diesen Verkauf gibt es auf dieser Kasse nicht.');
      const lines = correctableLines(fresh);
      // Korrigieren fragt nach keinem Grund: Die Kasse setzt „correction“ selbst (U17d)
      await store.voidSale(fresh.clientUuid, undefined, { reason: 'correction' });
      setCorrection({
        registerSeq: fresh.registerSeq,
        paidCents: paidCents(fresh),
        ...(fresh.payment === 'card' ? { payment: 'card' as const } : {}),
      });
      pos.refresh();
      pos.syncNow();
      await reloadLines(lines);
    } catch (caught) {
      setMessage({
        kind: 'error',
        text: caught instanceof Error ? caught.message : messageFor(caught),
      });
    }
  }

  /** Beim Verlassen der Kasse gehen geparkte Körbe verloren: vorher fragen (es ist kein Geld betroffen). */
  function leave(event: SyntheticEvent) {
    setPanel(null);
    if (parked.length === 0) return;
    const text = `${String(parked.length)} ${parked.length === 1 ? 'geparkter Korb wird' : 'geparkte Körbe werden'} verworfen (kein Geld betroffen). Fortfahren?`;
    if (!window.confirm(text)) {
      event.preventDefault();
      return;
    }
    parkedCorrections.current.clear();
    void store?.discardParked();
  }

  /** Antwort auf „Kasse verlassen?“: Parken rettet den Korb (Warteschlange), Verwerfen leert ihn, Bleiben macht nichts. */
  function answerLeave(choice: 'park' | 'discard' | 'stay') {
    chooseLeaveAsk(false);
    if (choice === 'stay') {
      focusField();
      releaseHeld();
      return;
    }
    if (choice === 'discard') {
      commitCart([]);
      setCorrection(null);
      clearUndo();
      leaveRegister(canGoBackRef.current);
      return;
    }
    void queue.push(async () => {
      await parkNow();
      // Misslang das Parken (Meldung steht), bleibt der Warenkorb und die Kasse offen; Gemerktes wird nachgeholt
      if (cartRef.current.length === 0) leaveRegister(canGoBackRef.current);
      else releaseHeld();
    });
  }

  /**
   * Unbekannter Code bei ausgeschaltetem freiem Betrag: Der Artikel kann nach dem letzten Abgleich neu dazugekommen sein.
   * Stand abgleichen, danach den Code erneut prüfen (siehe Effekt auf `pos.version`).
   */
  function updateAndRecheck() {
    if (warning === null) return;
    recheckRef.current = warning.code;
    cancelWarning();
    pos.syncNow();
    pos.refresh();
  }

  function cancelWarning() {
    const answered = warningRef.current;
    if (answered !== null) unparkedRef.current.delete(answered.code);
    chooseWarning(null);
    focusField();
    nextSheetWarning();
    releaseHeld(answered?.code);
  }

  /**
   * Startet die Aktion von Hand (X7, nach Ansage) oder beendet sie (`null`). Die Zeilen im Warenkorb werden neu berechnet, immer
   * vom Listenpreis aus. Läuft in der Scan-Warteschlange: ein gleichzeitig eintreffender Scan sieht nie halbe Preise. Nie unter
   * offenem Bezahl-Blatt.
   */
  function requestPromo(promo: PromoView | null, endedText?: string) {
    void queue.push(async () => {
      if (store === null || stageRef.current === 'pay') return;
      await store.setActivePromo(promo === null ? null : promo.id);
      promoRef.current = promo;
      clearUndo();
      setPromoState((state) => ({ ...state, activeId: promo === null ? null : promo.id }));
      commitCart(repriceCart(cartRef.current, promo));
      setMessage({
        kind: 'info',
        text:
          promo === null
            ? (endedText ?? 'Aktion beendet. Es gelten wieder die vollen Preise.')
            : `Aktion −${String(promo.basisPoints / 100)} % gestartet. Artikel ohne Einverständnis bleiben zum vollen Preis.`,
      });
    });
  }

  // Ende laut Snapshot erreicht (oder abgesagt): die Aktion schaltet sich selbst aus, die Preise kehren zurück. Nie unter dem
  // offenen Bezahl-Blatt: die Summe darf sich darunter nicht ändern.
  useEffect(() => {
    if (store === null || promoState.activeId === null || running !== null || stage === 'pay')
      return;
    requestPromo(null, 'Die Aktion ist beendet. Es gelten wieder die vollen Preise.');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- requestPromo nutzt nur Refs, Speicher und Zustandsfunktionen
  }, [store, promoState.activeId, running, stage]);

  function openPay() {
    if (cartRef.current.length === 0 || warningRef.current !== null || pos.revoked) return;
    // Ein gelesener, noch nicht übernommener Laufzettel fehlt in der Summe: erst übernehmen oder verwerfen
    if (sheetRef.current !== null) {
      setMessage({
        kind: 'info',
        text: 'Der Laufzettel ist noch nicht übernommen. Bitte „In den Warenkorb“ oder „Verwerfen“ wählen.',
      });
      return;
    }
    setPayError(null);
    clearUndo();
    go('pay');
  }

  async function checkout(givenCents: number | null, payment: 'cash' | 'card' = 'cash') {
    const lines = cartRef.current;
    if (store === null || lines.length === 0 || payLock.current || pos.revoked) return;
    payLock.current = true;
    setBusy(true);
    setPayError(null);
    try {
      const sale = await store.checkout(lines, givenCents, payment);
      // Die Suche zeigt den Artikel sofort als verkauft (der Weg beim Erfassen prüft ohnehin im Speicher)
      pos.catalog.setStatus(
        lines.flatMap((line) => (line.itemId === null ? [] : [line.itemId])),
        'sold',
      );
      // Erst nach dem festen Speichern gilt der Verkauf als bezahlt
      signalSuccess();
      setPaid({
        total: sale.totalCents,
        change: givenCents === null ? null : givenCents - sale.totalCents,
        ...(payment === 'card' ? { card: true } : {}),
        saleClientUuid: sale.clientUuid,
        registerSeq: sale.registerSeq,
      });
      justSoldRef.current = new Set(lines.map((line) => line.code));
      commitCart([]);
      setCorrection(null);
      clearUndo();
      setMessage(null);
      go('done');
      releaseHeld();
      pos.refresh();
      pos.syncNow();
    } catch (caught) {
      setPayError(messageFor(caught));
    } finally {
      payLock.current = false;
      setBusy(false);
    }
  }

  function nextCustomer() {
    setPaid(null);
    go('scan');
    focusField();
  }

  /** Fragt per Blatt nach (Nummer, Betrag, Rückgabe an den Kunden); erst „Stornieren“ löst den Storno aus. */
  function voidSale(sale: LocalSale, lineNo?: number, correct = false) {
    if (store === null) return;
    chooseVoidAsk({ sale, lineNo, correct });
  }

  async function answerVoid(yes: boolean, answer?: VoidAnswer) {
    const asked = voidAskRef.current;
    chooseVoidAsk(null);
    if (asked === null || !yes) {
      releaseHeld();
      return;
    }
    if (asked.correct === true) {
      // Über die Warteschlange: ein gleichzeitig eintreffender Scan sieht nie einen halb umgebauten Korb
      setPanel(null);
      void queue.push(() => correctNow(asked.sale));
      releaseHeld();
      return;
    }
    await doVoid(asked.sale, asked.lineNo, answer);
    releaseHeld();
  }

  async function doVoid(sale: LocalSale, lineNo?: number, answer?: VoidAnswer) {
    if (store === null) return;
    try {
      await store.voidSale(sale.clientUuid, lineNo, answer);
      pos.catalog.setStatus(
        sale.lines.flatMap((line, index) =>
          line.itemId === null || line.voided || (lineNo !== undefined && lineNo !== index + 1)
            ? []
            : [line.itemId],
        ),
        'received',
      );
      // Wie bei Bezahlen und Parken: Nach einem Geldvorgang gibt es kein Rückgängig für den Warenkorb mehr
      clearUndo();
      pos.refresh();
      pos.syncNow();
      if (stageRef.current === 'done' && lineNo === undefined) {
        setPaid(null);
        go('scan');
      }
      setMessage({ kind: 'info', text: 'Storniert. Die Artikel sind wieder verkäuflich.' });
    } catch (caught) {
      setMessage({
        kind: 'error',
        text: caught instanceof Error ? caught.message : messageFor(caught),
      });
    }
  }

  useUnloadGuard(cart.length > 0);
  useBackButton((canGoBack) => {
    canGoBackRef.current = canGoBack;
    const action = backAction({
      menuOpen: false,
      panelOpen:
        panel !== null ||
        itemLine !== undefined ||
        priceTarget !== undefined ||
        noteTarget !== null ||
        manualItemOpen ||
        customerOpen,
      stage: stageRef.current,
      warning: warningRef.current !== null,
      refConfirm: refConfirmRef.current !== null,
      parkHint: parkHintRef.current !== null,
      leaveAsk: leaveAskRef.current,
      voidAsk: voidAskRef.current !== null,
      cartCount: cartRef.current.length,
    });
    switch (action) {
      case 'close-menu':
        break;
      case 'close-panel':
        setPanel(null);
        setItemCode(null);
        setPriceCode(null);
        setNoteTarget(null);
        setManualItemOpen(false);
        setCustomerOpen(false);
        break;
      case 'close-pay':
        // Im Bestätigungsschritt geht die Taste nur einen Schritt zurück (zur Wahl des Betrags)
        if (payBackRef.current?.() === true) break;
        if (!payLock.current) {
          go('scan');
          focusField();
          releaseHeld();
        }
        break;
      case 'cancel-warning':
        cancelWarning();
        break;
      case 'cancel-ref':
        answerRef(false);
        break;
      case 'cancel-park-hint':
        answerParkHint('cancel');
        break;
      case 'close-leave':
        answerLeave('stay');
        break;
      case 'ask-leave':
        chooseLeaveAsk(true);
        break;
      case 'cancel-void':
        void answerVoid(false);
        break;
      case 'leave':
        leaveRegister(canGoBack);
        break;
    }
  });

  async function exportAll() {
    if (store === null) return;
    saveFile(
      new Blob([await store.exportAll()], { type: 'application/json' }),
      'kasse-notausgang.json',
    );
  }

  const offline = view.label === 'Offline';
  const canFullscreen = typeof document !== 'undefined' && document.fullscreenEnabled;

  return (
    <div className="cash" data-keep-scroll>
      <PosHeader
        title={pos.register?.label ?? 'Kasse'}
        view={view}
        parkedCount={parked.length}
        lastSale={lastSale}
        statusExtra={
          pos.stand?.snapshotAt == null
            ? undefined
            : `Artikelstand ${standTime.format(pos.stand.snapshotAt)}`
        }
        statusWarning={standInfo.kind !== 'ok' && standInfo.kind !== 'loading'}
        lastSaleStatus={lastSale === undefined ? undefined : sendLabel(lastSale, send)}
        sound={cameraOn ? undefined : { on: sound, onToggle: toggleSound }}
        onPark={() => {
          setPanel('park');
        }}
        onLast={() => {
          setPanel('last');
        }}
        onStatus={() => {
          setPanel('sync');
        }}
        onMenu={() => {
          setPanel('menu');
        }}
      />

      <section
        className={`cash__scan${cameraOn ? ' cash__scan--camera' : ''}`}
        aria-labelledby="erfassen"
      >
        <h2 id="erfassen" className="sr-only">
          Artikel erfassen
        </h2>
        <CameraScanner
          // Der Stand des Schalters kommt nach dem Start aus dem Speicher: Neustart der Kamera mit dem Laufzettel-Modus
          key={runSheetOn ? 'laufzettel' : 'etikett'}
          kind={runSheetOn ? 'labelOrRunSheet' : 'label'}
          {...(runSheetOn
            ? {}
            : {
                runSheetHint:
                  'Laufzettel ist in diesem Basar ausgeschaltet. Bitte einzeln scannen.',
              })}
          paused={modal}
          flash={flash}
          recent={modal ? [] : recent}
          sound={{ on: sound, onToggle: toggleSound }}
          toolsPlacement="overlay"
          retryable
          onAvailability={setCameraOn}
          onCode={(code) => {
            enqueue(code, 'camera');
          }}
        />
        <form className="cash__scan-row" onSubmit={handleScan} noValidate>
          <label htmlFor="pos-code" className="sr-only">
            Code (Scanner oder Tastatur)
          </label>
          <input
            id="pos-code"
            ref={input}
            // Ohne Maus keine Bildschirmtastatur, solange nur der Handscanner tippt; Antippen öffnet sie
            inputMode={fine || typing ? 'numeric' : 'none'}
            autoComplete="off"
            autoFocus
            placeholder="Code oder 17/5"
            value={scan}
            onClick={(click) => {
              if (fine || typing) return;
              // Im selben Tipp umschalten und neu fokussieren, sonst bliebe die Tastatur zu
              const field = click.currentTarget;
              field.inputMode = 'numeric';
              field.blur();
              field.focus();
              setTyping(true);
            }}
            onBlur={() => {
              setTyping(false);
            }}
            onChange={(change) => {
              setScan(change.target.value);
            }}
          />
          <Button type="submit">Erfassen</Button>
        </form>
        {!cameraOn && /\p{L}/u.test(scan) && !isRunSheetText(scan) && (
          <SearchResults
            result={inlineSearch}
            inCart={inCart}
            onAdd={(entry) => {
              setScan('');
              addFromSearch(entry);
            }}
            onNoLabel={
              noLabelOn
                ? () => {
                    setManualItemOpen(true);
                  }
                : undefined
            }
          />
        )}
      </section>

      <div className="cash__msg" aria-live="polite">
        <PromoBanner
          offered={offered}
          running={running}
          disabled={modal || pos.revoked}
          onStart={requestPromo}
          onStop={() => {
            requestPromo(null);
          }}
        />
        {pos.revoked && (
          <RevokedAlert onRetry={pos.retryUpload} onExport={() => void exportAll()} />
        )}
        {standBar !== null && <Alert kind="info">{standBar}</Alert>}
        {(sheet !== null || sheetResult !== null) && (
          <RunSheetCard
            assembly={sheet}
            result={sheetResult}
            disabled={modal}
            onImport={importSheet}
            onDiscard={discardSheet}
            onDismiss={() => {
              setSheetResult(null);
            }}
          />
        )}
        <InvoiceStatusBar
          entries={Object.entries(invoices.states)
            // Auf dem Bildschirm „Bezahlt“ zeigt der Kaufbeleg-Block den Stand dieses Verkaufs selbst
            .filter(([uuid]) => !(stage === 'done' && paid?.saleClientUuid === uuid))
            .map(([saleClientUuid, state]) => ({ saleClientUuid, state }))}
          offline={pos.connection === 'offline'}
          onDismiss={invoices.dismiss}
        />
        {message !== null && <Alert kind={message.kind}>{message.text}</Alert>}
      </div>

      <section className="cash__cart" aria-labelledby="korb">
        <div className="cash__listhead">
          <h2 id="korb" className="cash__listhead-title">
            <span className="sr-only">Warenkorb, </span>
            {`Erfasst (${String(cart.length)})`}
          </h2>
          <Button
            variant="secondary"
            className="cash__chip"
            aria-haspopup="dialog"
            onClick={() => {
              setPanel('search');
            }}
          >
            <Icon name="search" size={18} /> Suchen
          </Button>
        </div>
        {stage === 'done' && paid !== null ? (
          <PaidSummary
            paid={paid}
            modal={modal}
            onNext={nextCustomer}
            onCustomer={() => {
              setCustomerOpen(true);
            }}
          >
            {invoiceOn === true && !pos.deviceMode && paid.saleClientUuid !== undefined && (
              <InvoiceBox
                key={paid.saleClientUuid}
                state={invoices.states[paid.saleClientUuid]}
                offline={pos.connection === 'offline'}
                onSend={(email) =>
                  invoices.request(paid.saleClientUuid ?? '', email, paid.registerSeq)
                }
                onDismiss={() => {
                  invoices.dismiss(paid.saleClientUuid ?? '');
                }}
              />
            )}
          </PaidSummary>
        ) : cart.length === 0 ? (
          <p className="muted cash__empty">Noch nichts erfasst.</p>
        ) : (
          <ul className="list">
            {[...cart].reverse().map((line, position) => {
              // 1 = zuerst erfasst, neueste oben
              const no = cart.length - position;
              return (
                <CartRow
                  key={line.code}
                  line={line}
                  no={no}
                  flashing={highlight === line.code}
                  disabled={modal || stage !== 'scan'}
                  promoRunning={running !== null}
                  onRemove={() => {
                    requestRemove(line.code);
                  }}
                  onOpen={() => {
                    setItemCode(line.code);
                  }}
                />
              );
            })}
          </ul>
        )}
      </section>

      <footer className="cash__foot">
        <div className="cash__undo-live" role="status">
          {undo !== null && (
            <UndoBar
              key={undo.id}
              text={undoText(undo.entry)}
              onUndo={requestUndo}
              onExpire={clearUndo}
            />
          )}
        </div>
        {correction !== null && cart.length > 0 && (
          <p className="cash__correction">
            {`Korrektur zu Nr. ${String(correction.registerSeq)} (${euro(correction.paidCents)})`}
          </p>
        )}
        <p className="sr-only" aria-live="polite">{`Summe: ${euro(total)}`}</p>
        <p className="sr-only" role="status">
          {announce}
        </p>
        <div className="cash__foot-row">
          <Button
            className="cash__pay"
            aria-label={
              cart.length === 0
                ? undefined
                : `Kassieren, ${String(cart.length)} Artikel, ${euro(total).replace('€', 'Euro')}${saved > 0 ? `, ${euro(saved).replace('€', 'Euro')} gespart` : ''}`
            }
            disabled={cart.length === 0 || modal || pos.revoked || sheet !== null}
            onClick={openPay}
          >
            {cart.length === 0 ? (
              'Kassieren'
            ) : (
              // Zwei Zeilen statt Umbruch mitten im Text (schmale Handys); der a11y-Name steht im aria-label
              <>
                <span>Kassieren</span>
                <span className="cash__dot"> · </span>
                <span className="cash__pay-sub">{`${String(cart.length)} Artikel · ${euro(total)}${saved > 0 ? ` · ${euro(saved)} gespart` : ''}`}</span>
              </>
            )}
          </Button>
        </div>
        {cart.length === 0 && stage !== 'done' && (
          <p className="muted cash__hint">Etikett scannen oder Code eingeben</p>
        )}
      </footer>

      {refConfirm !== null && (
        <RefConfirmSheet
          sellerNumber={refConfirm.ref.sellerNumber}
          itemNo={refConfirm.ref.itemNo}
          line={refConfirm.line}
          onAnswer={answerRef}
        />
      )}
      {parkHint !== null && (
        <ParkHintSheet parkNo={parkHint.parkNo} line={parkHint.line} onAnswer={answerParkHint} />
      )}
      {panel === 'menu' && (
        <PosMenuSheet
          eventId={eventId}
          registerId={pos.register?.registerId}
          deviceMode={pos.deviceMode}
          offline={offline}
          auto={autoCapture.auto}
          onAuto={autoCapture.setAuto}
          sound={sound}
          onSound={toggleSound}
          canClear={cart.length > 0}
          canCustomer={cart.length > 0 || (stage === 'done' && paid !== null)}
          canFullscreen={canFullscreen}
          onAll={() => {
            setPanel('sales');
          }}
          onClear={() => {
            requestRemove();
          }}
          onNote={() => {
            setNoteTarget({});
          }}
          onNoLabel={
            noLabelOn
              ? () => {
                  setManualItemOpen(true);
                }
              : undefined
          }
          onCustomer={() => {
            setCustomerOpen(true);
          }}
          onDevice={() => {
            setPanel('device');
          }}
          onExport={() => void exportAll()}
          onFullscreen={() => {
            void document.documentElement.requestFullscreen().catch(() => undefined);
          }}
          onHandover={() => {
            setPanel('handover');
          }}
          onLeaveLink={leave}
          onClose={() => {
            setPanel(null);
          }}
        />
      )}
      {panel === 'park' && (
        <ParkSheet
          cartCount={cart.length}
          parked={parked}
          nowMs={nowMs}
          onPark={() => {
            setPanel(null);
            requestPark();
          }}
          onUnpark={(parkNo) => {
            setPanel(null);
            requestUnpark(parkNo);
          }}
          onClose={() => {
            setPanel(null);
          }}
        />
      )}
      {manualItemOpen && store !== null && (
        <ManualItemSheet
          sellerKnown={(number) => store.sellerKnown(number)}
          onAdd={addManualItem}
          onClose={() => {
            setManualItemOpen(false);
          }}
        />
      )}
      {panel === 'search' && (
        <SearchSheet
          index={pos.catalog}
          inCart={inCart}
          onAdd={addFromSearch}
          onCapture={(text) => {
            setPanel(null);
            enqueue(text, 'manual');
          }}
          onNoLabel={
            noLabelOn
              ? () => {
                  setPanel(null);
                  setManualItemOpen(true);
                }
              : undefined
          }
          onClose={() => {
            setPanel(null);
          }}
        />
      )}
      {itemLine !== undefined && (
        <ItemSheet
          line={itemLine}
          note={
            running !== null && (itemLine.promo === null || itemLine.promo === undefined)
              ? 'Keine Aktion für diesen Artikel.'
              : undefined
          }
          onChangePrice={
            priceChangeOn
              ? () => {
                  setPriceCode(itemLine.code);
                  setItemCode(null);
                }
              : undefined
          }
          onClearPrice={() => {
            setItemCode(null);
            requestPriceChange(itemLine.code, null);
          }}
          onRemove={() => {
            setItemCode(null);
            requestRemove(itemLine.code);
          }}
          onClose={() => {
            setItemCode(null);
          }}
        />
      )}
      {priceTarget !== undefined && (
        <PriceChangeSheet
          line={priceTarget}
          onApply={(cents, reason, note) => {
            setPriceCode(null);
            requestPriceChange(priceTarget.code, { cents, reason, note });
          }}
          onClose={() => {
            setPriceCode(null);
          }}
        />
      )}
      {leaveAsk && (
        <LeaveSheet
          count={cart.length}
          totalCents={total}
          canPark={parked.length < MAX_PARKED}
          onAnswer={answerLeave}
        />
      )}
      {warning !== null && (
        <WarningSheet
          warning={warning}
          noLabelOn={noLabelOn}
          manualPrice={manualPrice}
          error={warningError}
          onPrice={setManualPrice}
          onCancel={cancelWarning}
          onUpdate={updateAndRecheck}
          onManualItem={() => {
            cancelWarning();
            setManualItemOpen(true);
          }}
          onConfirm={confirm}
        />
      )}
      {stage === 'pay' && (
        <PaySheet
          total={total}
          lines={cart}
          backRef={payBackRef}
          correction={correction}
          busy={busy}
          error={payError}
          cardOffered={cardOffered}
          onPay={(givenCents, payment) => void checkout(givenCents, payment)}
          onClose={() => {
            // Während des Speicherns nicht: ein dann erfasster Artikel verschwände mit dem Leeren nach dem Kassieren
            if (payLock.current) return;
            go('scan');
            focusField();
            releaseHeld();
          }}
        />
      )}
      {panel === 'sync' && (
        <Sheet
          title="Verbindung und Stand"
          onClose={() => {
            setPanel(null);
          }}
        >
          <SyncDetails
            pos={pos}
            health={standInfo}
            onDeviceCheck={() => {
              setPanel('device');
            }}
            onNote={() => {
              setNoteTarget({});
            }}
          />
        </Sheet>
      )}
      {panel === 'last' && lastSale !== undefined && (
        <LastSaleSheet
          sale={lastSale}
          send={send}
          cartEmpty={cart.length === 0}
          onVoid={(sale, lineNo) => {
            voidSale(sale, lineNo);
          }}
          onCorrect={(sale) => {
            voidSale(sale, undefined, true);
          }}
          onAll={() => {
            setPanel('sales');
          }}
          onNote={(sale) => {
            setNoteTarget({
              saleClientUuid: sale.clientUuid,
              label: `Verkauf Nr. ${String(sale.registerSeq)}`,
            });
          }}
          notes={
            store === null ? null : (
              <SaleNoteStatus
                store={store}
                saleClientUuid={lastSale.clientUuid}
                version={pos.version}
              />
            )
          }
          onClose={() => {
            setPanel(null);
          }}
        />
      )}
      {panel === 'sales' && store !== null && (
        <SalesSheet
          store={store}
          version={pos.version}
          send={send}
          cartEmpty={cart.length === 0}
          onVoid={(sale, lineNo) => {
            voidSale(sale, lineNo);
          }}
          onCorrect={(sale) => {
            voidSale(sale, undefined, true);
          }}
          onNote={(sale) => {
            setNoteTarget({
              saleClientUuid: sale.clientUuid,
              label: `Verkauf Nr. ${String(sale.registerSeq)}`,
            });
          }}
          onClose={() => {
            setPanel(null);
          }}
        />
      )}
      {noteTarget !== null && store !== null && (
        <NoteSheet
          target={noteTarget}
          messagesHref={pos.deviceMode || offline ? undefined : '/nachrichten'}
          onSend={(topics, text) => {
            void store
              .addNote({
                saleClientUuid: noteTarget.saleClientUuid,
                lineNo: noteTarget.lineNo,
                topics,
                text,
              })
              .then(() => {
                setNoteTarget(null);
                pos.refresh();
                pos.syncNow();
                setMessage({
                  kind: 'info',
                  text: 'Gespeichert. Die Leitung bekommt es mit dem nächsten Abgleich.',
                });
              })
              .catch((caught: unknown) => {
                setMessage({ kind: 'error', text: messageFor(caught) });
              });
          }}
          onClose={() => {
            setNoteTarget(null);
          }}
        />
      )}
      {panel === 'device' && (
        <Sheet
          title="Geräte-Check"
          onClose={() => {
            setPanel(null);
          }}
        >
          <DeviceCheck pos={pos} />
        </Sheet>
      )}
      {panel === 'handover' && (
        <HandoverSheet
          pos={pos}
          eventId={eventId}
          cartEmpty={cart.length === 0}
          parkedCount={parked.length}
          offline={offline}
          discardParked={async () => {
            parkedCorrections.current.clear();
            await store?.discardParked();
          }}
          onClose={() => {
            setPanel(null);
          }}
        />
      )}
      {customerOpen && (
        <CustomerView
          totalCents={stage === 'done' && paid !== null ? paid.total : total}
          changeCents={stage === 'done' && paid !== null ? paid.change : null}
          paid={stage === 'done' && paid !== null}
          promoPercent={running === null ? null : running.basisPoints / 100}
          alreadyPaidCents={stage === 'done' || correction === null ? null : correction.paidCents}
          rotated={customerRotated}
          onRotate={() => {
            setCustomerRotated((current) => !current);
          }}
          onClose={() => {
            setCustomerOpen(false);
          }}
        />
      )}
      {/* Zuletzt: Die Rückfrage liegt über dem Blatt, aus dem sie kommt („Letzter Verkauf“, „Verkäufe“) */}
      {voidAsk !== null && (
        <VoidConfirmSheet
          sale={voidAsk.sale}
          lineNo={voidAsk.lineNo}
          correct={voidAsk.correct === true}
          refundCents={refundCents(voidAsk.sale, voidAsk.lineNo)}
          onAnswer={(yes, answer) => void answerVoid(yes, answer)}
        />
      )}
    </div>
  );
}
